import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant;
  size?: Size;
  /** Shows a spinner and blocks clicks. Keeps width stable by keeping children. */
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const SIZES: Record<Size, { padding: string; fontSize: string; minHeight: string }> = {
  sm: { padding: '0 var(--sp-3)', fontSize: 'var(--fs-body-sm)', minHeight: '32px' },
  md: { padding: '0 var(--sp-4)', fontSize: 'var(--fs-body-md)', minHeight: '40px' },
  lg: { padding: '0 var(--sp-5)', fontSize: 'var(--fs-body-lg)', minHeight: '48px' },
};

const VARIANTS: Record<Variant, React.CSSProperties> = {
  primary: {
    background: 'var(--accent)',
    color: '#fff',
    border: '1px solid var(--accent)',
  },
  secondary: {
    background: 'var(--surface)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--accent)',
    border: '1px solid transparent',
  },
  danger: {
    background: 'var(--error)',
    color: '#fff',
    border: '1px solid var(--error)',
  },
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  style,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const dims = SIZES[size];

  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      style={{
        ...VARIANTS[variant],
        ...dims,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-2)',
        width: fullWidth ? '100%' : undefined,
        borderRadius: 'var(--r-md)',
        fontFamily: 'var(--font-body)',
        fontWeight: 600,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.55 : 1,
        transition: 'background 120ms ease, opacity 120ms ease',
        ...style,
      }}
    >
      {loading && <Spinner size={14} color={variant === 'primary' || variant === 'danger' ? '#fff' : 'var(--accent)'} />}
      {children}
    </button>
  );
}
